import { FontIcon, Icon, Text, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import generalModule from "../../../../../../../utilities/GeneralModule";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { logger } from "../../../../../../../utilities/logger";
import { dropZoneStyleSets } from "../../../../../config/controlStyles";
import { IPageSettings } from "../../../../../interfaces/IPageSettings";
import { IStoreItem } from "../../../../../interfaces/IStoredItem";
import { setContentManagerSearchValue, toggleContentManagerReload } from "../../../../../redux/modules/contentManagerDirectory";
import { addUploadedFiles } from "../../../../../redux/modules/file";
import { setOneDriveDetails, setWorkingDirectories } from "../../../../../redux/modules/workingDirectory";
import { getOneDriveRootDetails } from "../../../../../services/GraphService";
import { homeBrandIconStyles, homeFontIconStyles, homeFontIconStylesMedium } from "../../../../../styles/iconStyles";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { FolderTypeIconEnum } from "../../../../enums/FolderTypeIconEnum";
import { ImageTypeEnum } from "../../../../enums/ImageTypeEnum";
import { FolderTypeIconQuery } from "../../../../helpers/folderTypeIconQuery";
import { RootState } from "../../../redux/storeMain";
import { ImageSizeEnum } from "../../../../enums/ImageSizeEnum";
import { ComponentTypes } from "../../../../../config/config";
import { OneDriveUrlHelper } from "../../../../helpers/oneDriveUrlHelper";
import { MoreVertical20Regular } from "../../SvgIcons/MoreVertical20Regular";
import { Save20Regular } from "../../SvgIcons/Save20Regular";

interface IHomeFolderItemProps {
    homeItem: IStoreItem;
    isMobile: boolean;
    isRecentList: boolean;
    isSuggestedList: boolean;
    hasFileSelected: boolean;
    isValidDropZone: boolean;
    setContextAwareLoading: (isLoading: boolean, contentManagerOnly?: boolean) => void;
    IsFolderInValidState: (item: IStoreItem, isRecentList: boolean) => Promise<boolean>;
    getPath: (workingDirectories: IPageSettings[], folderTypeIcon: FolderTypeIconEnum, includeBrackets?: boolean) => string;
    save: (storeItem: IStoreItem) => Promise<void>;
    openFolderOptions: (item: IStoreItem, isRecentList: boolean, isSuggestedList?: boolean) => void;
}

export const HomeFolderItem = ({ homeItem, isMobile, isRecentList, isSuggestedList, hasFileSelected, isValidDropZone, setContextAwareLoading, IsFolderInValidState, getPath, save, openFolderOptions }: IHomeFolderItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);

    const folderIcon = FolderTypeIconQuery.getImage(homeItem.folderTypeIcon);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event, false, false, homeItem)) {
            event.preventDefault();
            event.stopPropagation();
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, [homeItem]);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setIsDraggedOver(false);

        try {
            if (DragAndDropHelper.validateDroppedFiles(event)) {
                setContextAwareLoading(true);
                try {
                    dispatch(addUploadedFiles(await DragAndDropHelper.handleDroppedFiles(event, homeItem)));
                    if (await IsFolderInValidState(homeItem, isRecentList)) {
                        if (generalModule.isInConentMangerMode()) {
                            generalModule.setOpenPanelUploadPanel(true);
                            browseLocation();
                        } else {
                            save(homeItem);
                        }
                    }
                } catch (error) {
                    logger(error, "homeItem.tsx (handleDrop)");
                }
                setContextAwareLoading(false);
            }
            else if (DragAndDropHelper.validateDroppedGridItems(event, false, false, homeItem)) {
                const homeItemCurrentDirectory = homeItem.locations[homeItem.locations.length - 1];
                const homeItemIsInOneDrive = homeItemCurrentDirectory.type == ComponentTypes.OneDrive || homeItemCurrentDirectory.type == ComponentTypes.OneDriveSubFolder;
                const siteUrl = homeItemIsInOneDrive ? OneDriveUrlHelper.getSiteUrl(homeItemCurrentDirectory.webUrl) : homeItemCurrentDirectory.webUrl

                const origin = new URL(homeItemCurrentDirectory.webUrl).origin;
                const homeItemServerRelPath = `${homeItemCurrentDirectory.webUrl.replace(origin, "")}/${homeItemCurrentDirectory.directory}`;

                DragAndDropHelper.destinationDirectory = homeItemCurrentDirectory;
                DragAndDropHelper.handleGridMove(dispatch, siteUrl, homeItem.locations, homeItemIsInOneDrive, homeItemServerRelPath);
            }
        } catch (error) {
            logger(error, "HomeFolderItem.tsx (handleDrop)");
        }
    }, [homeItem, isRecentList, currentDirectory]);

    const className = React.useMemo((): string => {
        let className: string = "list-item";
        if (isDraggedOver) {
            className += ` dropzone ${dropZoneStyleSets.dropzoneActive}`;
        }
        else if (isValidDropZone) {
            className += ` dropzone`;
        }
        return className;
    }, [isValidDropZone, isDraggedOver]);

    const folderStyles: React.CSSProperties = isMobile ? mobileListItemStyle : {};

    const browseLocation = async () => {
        try {
            if (await IsFolderInValidState(homeItem, isRecentList)) {
                setContextAwareLoading(true);

                if (!oneDriveRootDetails && (homeItem.folderTypeIcon === FolderTypeIconEnum.oneDrive || homeItem.folderTypeIcon === FolderTypeIconEnum.oneDriveSubFolder)) {
                    dispatch(setOneDriveDetails(await getOneDriveRootDetails()));
                }

                if (!generalModule.isInConentMangerMode()) {
                    setContextAwareLoading(false);
                }

                dispatch(setWorkingDirectories(homeItem.locations));

                if (generalModule.isInConentMangerMode()) {
                    dispatch(setContentManagerSearchValue(""));
                    dispatch(toggleContentManagerReload());
                    setContextAwareLoading(false);
                }
            }
        } catch (error) {
            setContextAwareLoading(false);
            logger(error, "homeItem.tsx (onClick - item)");
        }
    }

    const getFontIconClassName = (fontIcon): string => {
        if (fontIcon.imageSize === ImageSizeEnum.medium) {
            return homeFontIconStylesMedium;
        }
        return homeFontIconStyles;
    }

    return (
        <div
            className={className}
            style={folderStyles}
            onDragEnter={isValidDropZone ? dragEnter : undefined}
            onDragOver={isValidDropZone ? dragOver : undefined}
            onDragLeave={isValidDropZone ? dragLeave : undefined}
            onDrop={isValidDropZone ? handleDrop : undefined}
            onClick={(e) => {
                e.stopPropagation();
                browseLocation();
            }}
        >
            <div className="list-item-link">
                <TooltipHost content={getPath(homeItem.locations, homeItem.folderTypeIcon)}>
                    <div className="list-item-icon">
                        {folderIcon.imageType === ImageTypeEnum.fileType
                            ? <Icon {...getFileTypeIconProps({ type: folderIcon.fileIconType, size: 20, imageFileType: "svg" })} />
                            : folderIcon.imageType === ImageTypeEnum.url
                                ? <img src={folderIcon.location} className={homeBrandIconStyles} width={20} height={20} />
                                : folderIcon.svgComponent
                        }
                    </div>
                </TooltipHost>
                <Text className="list-item-name">
                    <TooltipHost content={homeItem.name} overflowMode={TooltipOverflowMode.Parent}>
                        {homeItem.name}
                    </TooltipHost>
                    <div className="xb-grey-text text-ellipsis">
                        <TooltipHost
                            content={getPath(homeItem.locations, homeItem.folderTypeIcon, false)}
                            overflowMode={TooltipOverflowMode.Parent}
                            style={{ maxWidth: "90vw", wordBreak: "break-all" }}>
                            {getPath(homeItem.locations, homeItem.folderTypeIcon, false)}
                        </TooltipHost>
                    </div>
                </Text>
            </div>
            <>
                {hasFileSelected && isValidDropZone && (
                    <TooltipHost content={languageStrings.SaveTo + homeItem.name}>
                        <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                            {/* <FontIcon
                                iconName="Save"
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    setContextAwareLoading(true);
                                    try {
                                        if (await IsFolderInValidState(homeItem, isRecentList)) {
                                            save(homeItem);
                                        } else {
                                            setContextAwareLoading(false);
                                        }
                                    } catch (error) {
                                        logger(error, "homeItem.tsx (onClick - save Icon)");
                                        setContextAwareLoading(false);
                                    }
                                }}
                            /> */}
                            <Save20Regular
                                onClick={async (e: any) => {
                                    e.stopPropagation();
                                    setContextAwareLoading(true);
                                    try {
                                        if (await IsFolderInValidState(homeItem, isRecentList)) {
                                            save(homeItem);
                                        } else {
                                            setContextAwareLoading(false);
                                        }
                                    } catch (error) {
                                        logger(error, "homeItem.tsx (onClick - save Icon)");
                                        setContextAwareLoading(false);
                                    }
                                }}
                            />
                        </div>
                    </TooltipHost>
                )}
                <TooltipHost content={languageStrings.MoreActions}>
                    <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                        {/* <FontIcon
                            iconName="More"
                            onClick={(e) => {
                                e.stopPropagation();
                                openFolderOptions(homeItem, isRecentList, isSuggestedList);
                            }}
                        /> */}
                        <MoreVertical20Regular
                            onClick={(e) => {
                                e.stopPropagation();
                                openFolderOptions(homeItem, isRecentList, isSuggestedList);
                            }}
                        />
                    </div>
                </TooltipHost>
            </>
        </div>
    )
};